import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import {
  View,
  TextInput,
  Pressable,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
} from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";
import { useStorage } from "@/hooks/useStorage";
import useContextMenu from "@/hooks/useContextMenu";
import { DateTimePicker } from "@/components/Pickers/DatetimePicker";
import { parseFlexibleDate } from "@/lib/dateParser";
import {
  formatTime12,
  formatDateEditable,
  formatDuration,
  parseDurationText,
  parseTimeText,
  applyTimeParts,
  applyDateParts,
} from "@/lib/dateTimeInput";
import { shiftsService } from "@/services/shiftService";
import { Skeleton } from "@/components/ui/Skeleton";

// ── Types ────────────────────────────────────────────────────

export type HeatmapShift = {
  startTime: string; // ISO string
  endTime: string; // ISO string
  label?: string; // e.g. "Morning crew" — for future tooltip use
};

export interface ShiftTimeInputProps {
  startTime: string | null;
  endTime: string | null;
  onChange: (values: { startTime: string; endTime: string }) => void;
  editing?: boolean;
  loading?: boolean;
  label?: string;
  heatmapShifts?: HeatmapShift[];
  enableHeatmap?: boolean;
  excludeShiftId?: string;
}

// ── Constants ────────────────────────────────────────────────

const DURATION_PRESETS = [
  { label: "4h", minutes: 240 },
  { label: "6h", minutes: 360 },
  { label: "8h", minutes: 480 },
  { label: "10h", minutes: 600 },
  { label: "12h", minutes: 720 },
];

const STEP_MINUTES = 30;

const HEATMAP_VISIBLE_KEY = "shift-heatmap-visible";

const DAY_CELL_WIDTH = 36;
const DAY_CELL_GAP = 4;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ── Helpers ──────────────────────────────────────────────────

function formatDateDisplay(d: Date): string {
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function getDurationMin(start: Date, end: Date): number {
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / (1000 * 60)));
}

// ── Heatmap Constants ────────────────────────────────────────

const HEATMAP_START_HOUR = 6; // 6 AM
const HEATMAP_END_HOUR = 24; // midnight
const HEATMAP_SLOTS = (HEATMAP_END_HOUR - HEATMAP_START_HOUR) * 2; // 36 half-hour slots
const HEATMAP_HOUR_LABELS = Array.from({ length: (HEATMAP_END_HOUR - HEATMAP_START_HOUR) / 2 + 1 }, (_, i) => {
  const h = HEATMAP_START_HOUR + i * 2;
  if (h === 0 || h === 24) return "12a";
  if (h === 12) return "12p";
  return h < 12 ? `${h}a` : `${h - 12}p`;
});

// ── ShiftHeatmap Sub-component ──────────────────────────────

function ShiftHeatmap({
  shifts,
  currentStart,
  currentEnd,
  colors,
}: {
  shifts: HeatmapShift[];
  currentStart: Date;
  currentEnd: Date;
  colors: ReturnType<typeof useTheme>["colors"];
}) {
  const density = useMemo(() => {
    const slots = new Array(HEATMAP_SLOTS).fill(0);
    const dayStart = new Date(currentStart);
    dayStart.setHours(HEATMAP_START_HOUR, 0, 0, 0);

    for (const shift of shifts) {
      const sStart = new Date(shift.startTime);
      const sEnd = new Date(shift.endTime);

      for (let i = 0; i < HEATMAP_SLOTS; i++) {
        const slotStart = new Date(dayStart.getTime() + i * 30 * 60 * 1000);
        const slotEnd = new Date(slotStart.getTime() + 30 * 60 * 1000);
        if (sStart < slotEnd && sEnd > slotStart) {
          slots[i]++;
        }
      }
    }
    return slots;
  }, [shifts, currentStart]);

  const overlayStyle = useMemo(() => {
    const dayStart = new Date(currentStart);
    dayStart.setHours(HEATMAP_START_HOUR, 0, 0, 0);
    const dayEnd = new Date(dayStart.getTime() + HEATMAP_SLOTS * 30 * 60 * 1000);

    const clampedStart = new Date(Math.max(currentStart.getTime(), dayStart.getTime()));
    const clampedEnd = new Date(Math.min(currentEnd.getTime(), dayEnd.getTime()));

    const totalMs = dayEnd.getTime() - dayStart.getTime();
    const leftPct = ((clampedStart.getTime() - dayStart.getTime()) / totalMs) * 100;
    const widthPct = ((clampedEnd.getTime() - clampedStart.getTime()) / totalMs) * 100;

    return { left: `${leftPct}%`, width: `${widthPct}%` } as const;
  }, [currentStart, currentEnd]);

  const slotColor = (count: number): string => {
    if (count === 0) return colors.backgroundSecondary;
    if (count === 1) return colors.amber.background;
    if (count === 2) return colors.amber.border;
    return colors.red.background;
  };

  return (
    <View style={heatStyles.heatmapContainer}>
      <View style={heatStyles.heatmapBar}>
        {density.map((count, i) => (
          <View key={i} style={[heatStyles.heatmapSlot, { backgroundColor: slotColor(count) }]} />
        ))}
        <View
          style={[
            heatStyles.heatmapOverlay,
            {
              left: overlayStyle.left,
              width: overlayStyle.width,
              borderColor: colors.primary,
              backgroundColor: colors.primary + "26",
            },
          ]}
        />
      </View>
      <View style={heatStyles.heatmapLabels}>
        {HEATMAP_HOUR_LABELS.map((lbl, i) => (
          <ThemedText key={i} style={[heatStyles.heatmapLabel, { color: colors.textSecondary }]}>
            {lbl}
          </ThemedText>
        ))}
      </View>
      <View style={heatStyles.heatmapBadge}>
        <MaterialCommunityIcons name="account-group-outline" size={12} color={colors.textSecondary} />
        <ThemedText style={[heatStyles.heatmapLabel, { color: colors.textSecondary }]}>
          {shifts.length} other shift{shifts.length !== 1 ? "s" : ""}
        </ThemedText>
      </View>
    </View>
  );
}

// ── DayHeatmap Sub-component ────────────────────────────────

function DayHeatmap({
  shifts,
  selectedDate,
  onSelectDate,
  colors,
}: {
  shifts: HeatmapShift[];
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  colors: ReturnType<typeof useTheme>["colors"];
}) {
  const scrollRef = useRef<ScrollView>(null);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const days = useMemo(() => {
    const result: Date[] = [];
    for (let i = -14; i < 14; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      result.push(d);
    }
    return result;
  }, [today]);

  const dayCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const shift of shifts) {
      const d = new Date(shift.startTime);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [shifts]);

  const selectedKey = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const selectedIndex = days.findIndex((d) => {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return key === selectedKey;
  });

  useEffect(() => {
    if (selectedIndex >= 0 && scrollRef.current) {
      const offset = selectedIndex * (DAY_CELL_WIDTH + DAY_CELL_GAP) - 100;
      scrollRef.current.scrollTo({ x: Math.max(0, offset), animated: false });
    }
  }, [selectedIndex]);

  const cellColor = (count: number): string => {
    if (count === 0) return colors.backgroundSecondary;
    if (count === 1) return colors.emerald.background;
    if (count === 2) return colors.emerald.border;
    return colors.green.background;
  };

  return (
    <ScrollView ref={scrollRef} horizontal showsHorizontalScrollIndicator={false}>
      <View style={heatStyles.dayHeatmapRow}>
        {days.map((day) => {
          const key = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
          const count = dayCounts.get(key) ?? 0;
          const isSelected = key === selectedKey;
          const isToday = key === todayKey;

          return (
            <Pressable
              key={key}
              onPress={() => onSelectDate(day)}
              style={[
                heatStyles.dayCell,
                { backgroundColor: cellColor(count) },
                isSelected && [heatStyles.dayCellSelected, { borderColor: colors.primary }],
              ]}
            >
              <ThemedText style={[heatStyles.dayCellNumber, { color: isSelected ? colors.primary : colors.text }]}>
                {day.getDate()}
              </ThemedText>
              <ThemedText style={[heatStyles.dayCellWeekday, { color: colors.textSecondary }]}>
                {WEEKDAYS[day.getDay()]}
              </ThemedText>
              {isToday && !isSelected && <View style={[heatStyles.todayDot, { backgroundColor: colors.primary }]} />}
            </Pressable>
          );
        })}
      </View>
    </ScrollView>
  );
}

// ── useHeatmapShifts hook ───────────────────────────────────

function useHeatmapShifts(enabled: boolean, excludeShiftId?: string) {
  const [shifts, setShifts] = useState<HeatmapShift[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - 14);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now);
      end.setDate(now.getDate() + 14);
      end.setHours(23, 59, 59, 999);

      const rows = await shiftsService.getShiftHeatmap({
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        excludeShiftId,
      });

      setShifts(
        rows
          .filter((r) => r.start_time && r.end_time)
          .map((r) => ({
            startTime: r.start_time,
            endTime: r.end_time,
            label: r.name,
          })),
      );
    } catch (err) {
      console.error("Heatmap fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, [enabled, excludeShiftId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { shifts, loading, refresh: fetch };
}

// ── Component ────────────────────────────────────────────────

export function ShiftTimeInput({
  startTime,
  endTime,
  onChange,
  editing = true,
  loading = false,
  label = "Shift Schedule",
  heatmapShifts: heatmapShiftsProp,
  enableHeatmap = false,
  excludeShiftId,
}: ShiftTimeInputProps) {
  const { colors } = useTheme();
  const storage = useStorage();
  const contextMenu = useContextMenu();
  const calendarRef = useRef<View>(null);

  // ── Heatmap: independent fetch when enableHeatmap is true ──
  const { shifts: fetchedShifts, loading: heatmapLoading } = useHeatmapShifts(enableHeatmap && editing, excludeShiftId);
  const heatmapShifts = enableHeatmap ? fetchedShifts : heatmapShiftsProp;

  const [heatmapVisible, setHeatmapVisible] = useState(() => storage.getBoolean(HEATMAP_VISIBLE_KEY) ?? false);

  const toggleHeatmap = () => {
    const next = !heatmapVisible;
    setHeatmapVisible(next);
    storage.saveFast(HEATMAP_VISIBLE_KEY, next);
  };

  const start = useMemo(() => (startTime ? new Date(startTime) : null), [startTime]);
  const end = useMemo(() => (endTime ? new Date(endTime) : null), [endTime]);
  const durationMin = useMemo(() => (start && end ? getDurationMin(start, end) : 0), [start, end]);

  // ── Text state for date & time inputs ──

  const [dateText, setDateText] = useState(start ? formatDateEditable(start) : "");
  const [timeText, setTimeText] = useState(start ? formatTime12(start) : "");
  const [durationText, setDurationText] = useState(durationMin ? formatDuration(durationMin) : "");

  // Sync text when external values change
  useEffect(() => {
    setDateText(start ? formatDateEditable(start) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime]);

  useEffect(() => {
    setTimeText(start ? formatTime12(start) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime]);

  useEffect(() => {
    setDurationText(durationMin ? formatDuration(durationMin) : "");
  }, [durationMin]);

  // ── Date commit (blur / Enter) ──

  const commitDate = (text: string) => {
    if (!text.trim()) {
      // Reset to current value
      setDateText(start ? formatDateEditable(start) : "");
      return;
    }
    const parsed = parseFlexibleDate(text, { mode: "date", baseDate: start ?? new Date() });
    if (parsed) {
      const newStart = applyDateParts(start ?? new Date(), parsed);
      const dur = durationMin || 480;
      const newEnd = new Date(newStart.getTime() + dur * 60 * 1000);
      onChange({ startTime: newStart.toISOString(), endTime: newEnd.toISOString() });
      setDateText(formatDateEditable(newStart));
    } else {
      // Revert
      setDateText(start ? formatDateEditable(start) : "");
    }
  };

  // ── Time commit (blur / Enter) ──

  const commitTime = (text: string) => {
    if (!text.trim()) {
      setTimeText(start ? formatTime12(start) : "");
      return;
    }
    const parsed = parseTimeText(text);
    if (parsed && start) {
      const newStart = applyTimeParts(start, parsed);
      const dur = durationMin || 480;
      const newEnd = new Date(newStart.getTime() + dur * 60 * 1000);
      onChange({ startTime: newStart.toISOString(), endTime: newEnd.toISOString() });
      setTimeText(formatTime12(newStart));
    } else {
      setTimeText(start ? formatTime12(start) : "");
    }
  };

  // ── Key handlers ──

  const handleDateKeyPress = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (e.nativeEvent.key === "Enter") {
      e.preventDefault();
      commitDate(dateText);
    }
  };

  const handleTimeKeyPress = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (e.nativeEvent.key === "Enter") {
      e.preventDefault();
      commitTime(timeText);
    }
  };

  // ── Duration commit (blur / Enter) ──

  const commitDuration = (text: string) => {
    if (!text.trim() || !start) {
      setDurationText(durationMin ? formatDuration(durationMin) : "");
      return;
    }
    const parsed = parseDurationText(text, STEP_MINUTES);
    if (parsed !== null) {
      const newEnd = new Date(start.getTime() + parsed * 60 * 1000);
      onChange({ startTime: start.toISOString(), endTime: newEnd.toISOString() });
      setDurationText(formatDuration(parsed));
    } else {
      setDurationText(durationMin ? formatDuration(durationMin) : "");
    }
  };

  const handleDurationKeyPress = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (e.nativeEvent.key === "Enter") {
      e.preventDefault();
      commitDuration(durationText);
    }
  };

  // ── Calendar picker ──

  const handleOpenCalendar = () => {
    if (!editing) return;
    calendarRef.current?.measureInWindow((x, y, w, h) => {
      contextMenu.setTriggerElement?.({ x, y, width: w ?? 0, height: h ?? 0 });
      contextMenu.setLocation({ x, y: y + (h ?? 0) + 4 });
      contextMenu.setMenuContent(
        <DateTimePicker
          date={start ?? undefined}
          hideTime
          fireOnDayPick
          onPick={(e) => {
            const picked = e.date;
            const base = start ?? new Date();
            const newStart = new Date(base);
            newStart.setFullYear(picked.getFullYear(), picked.getMonth(), picked.getDate());
            const dur = durationMin || 480;
            const newEnd = new Date(newStart.getTime() + dur * 60 * 1000);
            onChange({ startTime: newStart.toISOString(), endTime: newEnd.toISOString() });
            contextMenu.setVisible(false);
          }}
          onCancel={() => contextMenu.setVisible(false)}
        />,
      );
      contextMenu.setVisible(true);
    });
  };

  // ── Duration stepper ──

  const adjustDuration = (deltaMin: number) => {
    if (!start) return;
    const newDur = Math.max(STEP_MINUTES, durationMin + deltaMin);
    const newEnd = new Date(start.getTime() + newDur * 60 * 1000);
    onChange({ startTime: start.toISOString(), endTime: newEnd.toISOString() });
  };

  const setDurationPreset = (presetMin: number) => {
    if (!start) return;
    const newEnd = new Date(start.getTime() + presetMin * 60 * 1000);
    onChange({ startTime: start.toISOString(), endTime: newEnd.toISOString() });
  };

  // ── Day heatmap handler ──

  const handleSelectDay = (day: Date) => {
    const base = start ?? new Date();
    const newStart = new Date(day);
    newStart.setHours(base.getHours(), base.getMinutes(), 0, 0);
    const dur = durationMin || 480;
    const newEnd = new Date(newStart.getTime() + dur * 60 * 1000);
    onChange({ startTime: newStart.toISOString(), endTime: newEnd.toISOString() });
  };

  // ── Render ──

  return (
    <View style={styles.container}>
      <ThemedText style={[styles.label, { color: colors.textSecondary }]}>{label}</ThemedText>

      <View style={styles.row}>
        {/* ── DATE ── */}
        <View style={[styles.cell, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
          <View style={styles.cellHeader}>
            <MaterialCommunityIcons name="calendar" size={13} color={colors.textSecondary} />
            <ThemedText style={[styles.cellHeaderText, { color: colors.textSecondary }]}>DATE</ThemedText>
          </View>
          {loading ? (
            <Skeleton width={100} height={24} radius={4} />
          ) : editing ? (
            <View style={styles.inputWithButton}>
              <TextInput
                value={dateText}
                onChangeText={setDateText}
                onBlur={() => commitDate(dateText)}
                onKeyPress={handleDateKeyPress}
                placeholder="2/2/26, feb 2…"
                placeholderTextColor={colors.placeholder}
                style={[styles.textInput, { color: colors.text }]}
                autoCapitalize="none"
                autoCorrect={false}
                selectTextOnFocus
              />
              <Pressable
                ref={calendarRef}
                onPress={handleOpenCalendar}
                hitSlop={6}
                style={({ hovered }) => [styles.calendarBtn, hovered && { backgroundColor: colors.border + "80" }]}
              >
                <MaterialCommunityIcons name="calendar-search" size={16} color={colors.textSecondary} />
              </Pressable>
            </View>
          ) : start ? (
            <ThemedText style={[styles.primaryText, { color: colors.text }]}>{formatDateDisplay(start)}</ThemedText>
          ) : (
            <ThemedText style={[styles.placeholderText, { color: colors.placeholder }]}>No date</ThemedText>
          )}
        </View>

        {/* ── START TIME ── */}
        <View style={[styles.cell, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
          <View style={styles.cellHeader}>
            <MaterialCommunityIcons name="clock-outline" size={13} color={colors.textSecondary} />
            <ThemedText style={[styles.cellHeaderText, { color: colors.textSecondary }]}>START</ThemedText>
          </View>
          {loading ? (
            <Skeleton width={70} height={24} radius={4} />
          ) : editing ? (
            <TextInput
              value={timeText}
              onChangeText={setTimeText}
              onBlur={() => commitTime(timeText)}
              onKeyPress={handleTimeKeyPress}
              placeholder="8 am, 2:00 PM…"
              placeholderTextColor={colors.placeholder}
              style={[styles.textInput, { color: colors.text }]}
              autoCapitalize="none"
              autoCorrect={false}
              selectTextOnFocus
            />
          ) : start ? (
            <ThemedText style={[styles.primaryText, { color: colors.text }]}>{formatTime12(start)}</ThemedText>
          ) : (
            <ThemedText style={[styles.placeholderText, { color: colors.placeholder }]}>No time</ThemedText>
          )}
        </View>

        {/* ── DURATION ── */}
        <View style={[styles.cell, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
          <View style={styles.cellHeader}>
            <MaterialCommunityIcons name="timer-outline" size={13} color={colors.textSecondary} />
            <ThemedText style={[styles.cellHeaderText, { color: colors.textSecondary }]}>DURATION</ThemedText>
          </View>
          {loading ? (
            <Skeleton width={60} height={24} radius={4} />
          ) : start && end ? (
            <View style={styles.stepperRow}>
              {editing && (
                <Pressable
                  onPress={() => adjustDuration(-STEP_MINUTES)}
                  disabled={durationMin <= STEP_MINUTES}
                  style={({ hovered }) => [
                    styles.stepBtn,
                    {
                      backgroundColor: hovered ? colors.border : colors.background,
                      opacity: durationMin <= STEP_MINUTES ? 0.3 : 1,
                    },
                  ]}
                >
                  <MaterialCommunityIcons name="minus" size={14} color={colors.text} />
                </Pressable>
              )}
              {editing ? (
                <TextInput
                  value={durationText}
                  onChangeText={setDurationText}
                  onBlur={() => commitDuration(durationText)}
                  onKeyPress={handleDurationKeyPress}
                  placeholder="8h, 8.5, 8h 30m"
                  placeholderTextColor={colors.placeholder}
                  style={[styles.durationInput, { color: colors.text }]}
                  autoCapitalize="none"
                  autoCorrect={false}
                  selectTextOnFocus
                />
              ) : (
                <ThemedText style={[styles.durationText, { color: colors.text }]}>
                  {formatDuration(durationMin)}
                </ThemedText>
              )}
              {editing && (
                <Pressable
                  onPress={() => adjustDuration(STEP_MINUTES)}
                  style={({ hovered }) => [
                    styles.stepBtn,
                    { backgroundColor: hovered ? colors.border : colors.background },
                  ]}
                >
                  <MaterialCommunityIcons name="plus" size={14} color={colors.text} />
                </Pressable>
              )}
            </View>
          ) : (
            <ThemedText style={[styles.placeholderText, { color: colors.placeholder }]}>—</ThemedText>
          )}
        </View>
      </View>

      {/* ── Duration presets + heatmap toggle ── */}
      {editing && start && !loading && (
        <View style={styles.presetRow}>
          {DURATION_PRESETS.map((preset) => {
            const isActive = durationMin === preset.minutes;
            return (
              <Pressable
                key={preset.label}
                onPress={() => setDurationPreset(preset.minutes)}
                style={({ hovered }) => [
                  styles.presetPill,
                  {
                    backgroundColor: isActive
                      ? colors.primary + "20"
                      : hovered
                        ? colors.backgroundSecondary
                        : "transparent",
                    borderColor: isActive ? colors.primary : colors.border,
                  },
                ]}
              >
                <ThemedText
                  style={[
                    styles.presetText,
                    { color: isActive ? colors.primary : colors.textSecondary },
                    isActive && { fontWeight: "700" },
                  ]}
                >
                  {preset.label}
                </ThemedText>
              </Pressable>
            );
          })}
          {enableHeatmap && end && (
            <Pressable
              onPress={toggleHeatmap}
              disabled={heatmapLoading}
              style={({ hovered }) => [
                styles.heatmapToggle,
                { borderColor: colors.border },
                hovered && !heatmapLoading && { backgroundColor: colors.backgroundSecondary },
                heatmapLoading && { opacity: 0.6 },
              ]}
            >
              {heatmapLoading ? (
                <ActivityIndicator size={10} color={colors.textSecondary} />
              ) : (
                <MaterialCommunityIcons name="chart-bar" size={11} color={colors.textSecondary} />
              )}
              <ThemedText style={[styles.heatmapToggleText, { color: colors.textSecondary }]}>
                {heatmapLoading ? "Loading" : heatmapVisible ? "Hide" : "Show"}
              </ThemedText>
              {!heatmapLoading && (
                <MaterialCommunityIcons
                  name={heatmapVisible ? "chevron-up" : "chevron-down"}
                  size={13}
                  color={colors.textSecondary}
                />
              )}
            </Pressable>
          )}
        </View>
      )}

      {/* ── Heatmaps ── */}
      {editing && start && end && heatmapVisible && (
        <View style={[styles.heatmapWrapper, { borderColor: colors.border }]}>
          {heatmapLoading ? (
            <View style={styles.heatmapLoadingContainer}>
              <ActivityIndicator size="small" color={colors.primary} />
              <ThemedText style={[styles.heatmapLoadingText, { color: colors.textSecondary }]}>
                Loading shift data…
              </ThemedText>
            </View>
          ) : heatmapShifts && heatmapShifts.length > 0 ? (
            <>
              <DayHeatmap shifts={heatmapShifts} selectedDate={start} onSelectDate={handleSelectDay} colors={colors} />
              <ShiftHeatmap shifts={heatmapShifts} currentStart={start} currentEnd={end} colors={colors} />
            </>
          ) : (
            <View style={styles.heatmapEmptyContainer}>
              <MaterialCommunityIcons name="calendar-blank-outline" size={20} color={colors.textSecondary} />
              <ThemedText style={[styles.heatmapLoadingText, { color: colors.textSecondary }]}>
                No other shifts in this period
              </ThemedText>
            </View>
          )}
        </View>
      )}

      {/* ── Summary line ── */}
      {loading ? (
        <View style={[styles.summaryRow, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
          <Skeleton width={14} height={14} radius={3} />
          <Skeleton width={160} height={14} radius={4} />
          <Skeleton width={12} height={12} radius={3} />
          <Skeleton width={60} height={14} radius={4} />
          <View style={{ marginLeft: "auto" }}>
            <Skeleton width={50} height={18} radius={4} />
          </View>
        </View>
      ) : start && end ? (
        <View style={[styles.summaryRow, { backgroundColor: colors.backgroundSecondary, borderColor: colors.border }]}>
          <MaterialCommunityIcons name="clock-check-outline" size={14} color={colors.primary} />
          <ThemedText style={[styles.summaryText, { color: colors.text }]}>
            {formatDateDisplay(start)}, {formatTime12(start)}
          </ThemedText>
          <MaterialCommunityIcons name="arrow-right" size={12} color={colors.textSecondary} />
          <ThemedText style={[styles.summaryText, { color: colors.text }]}>{formatTime12(end)}</ThemedText>
          <View style={[styles.summaryBadge, { backgroundColor: colors.primary + "15" }]}>
            <ThemedText style={[styles.summaryBadgeText, { color: colors.primary }]}>
              {formatDuration(durationMin)}
            </ThemedText>
          </View>
        </View>
      ) : null}
    </View>
  );
}

// ── Styles ───────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  label: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  row: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  cell: {
    flex: 1,
    minWidth: 140,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    padding: 10,
    gap: 6,
    overflow: "hidden",
  },
  cellHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  cellHeaderText: {
    fontSize: 9,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  primaryText: {
    fontSize: 14,
    fontWeight: "600",
  },
  placeholderText: {
    fontSize: 13,
  },
  textInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    fontWeight: "600",
    padding: 0,
    // @ts-ignore web-only
    outlineStyle: "none",
  },
  inputWithButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  calendarBtn: {
    width: 26,
    height: 26,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  stepperRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  stepBtn: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  durationText: {
    fontSize: 14,
    fontWeight: "600",
    fontVariant: ["tabular-nums"],
  },
  durationInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    fontWeight: "600",
    fontVariant: ["tabular-nums"],
    padding: 0,
    textAlign: "center",
    // @ts-ignore web-only
    outlineStyle: "none",
  },
  presetRow: {
    flexDirection: "row",
    gap: 6,
  },
  presetPill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: StyleSheet.hairlineWidth,
  },
  presetText: {
    fontSize: 11,
    fontWeight: "600",
  },
  heatmapToggle: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: "auto",
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: StyleSheet.hairlineWidth,
  },
  heatmapToggleText: {
    fontSize: 10,
    fontWeight: "600",
  },
  heatmapWrapper: {
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    padding: 10,
    gap: 8,
    minHeight: 100,
  },
  heatmapLoadingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    minHeight: 80,
  },
  heatmapEmptyContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    minHeight: 60,
  },
  heatmapLoadingText: {
    fontSize: 12,
    fontWeight: "500",
  },
  heatmapLoading: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 12,
  },
  summaryRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  summaryText: {
    fontSize: 12,
    fontWeight: "500",
  },
  summaryBadge: {
    marginLeft: "auto",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  summaryBadgeText: {
    fontSize: 11,
    fontWeight: "700",
    fontVariant: ["tabular-nums"],
  },
});

const heatStyles = StyleSheet.create({
  heatmapContainer: {
    gap: 4,
  },
  heatmapBar: {
    flexDirection: "row",
    height: 20,
    borderRadius: 4,
    overflow: "hidden",
  },
  heatmapSlot: {
    flex: 1,
  },
  heatmapOverlay: {
    position: "absolute",
    top: 0,
    bottom: 0,
    borderRadius: 3,
    borderWidth: 2,
  },
  heatmapLabels: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  heatmapLabel: {
    fontSize: 9,
    fontVariant: ["tabular-nums"],
  },
  heatmapBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    alignSelf: "flex-end",
  },
  dayHeatmapRow: {
    flexDirection: "row",
    gap: DAY_CELL_GAP,
    paddingHorizontal: 4,
  },
  dayCell: {
    width: DAY_CELL_WIDTH,
    height: 48,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
  },
  dayCellSelected: {
    borderWidth: 2,
  },
  dayCellNumber: {
    fontSize: 13,
    fontWeight: "600",
  },
  dayCellWeekday: {
    fontSize: 9,
  },
  todayDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    position: "absolute",
    bottom: 3,
  },
});
